class LogeskiMobileActions extends HTMLElement {
  constructor() {
    super();
    this.variantId = this.dataset.variantId;
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onChatClick = this.onChatClick.bind(this);
  }

  connectedCallback() {
    this.productForm = document.getElementById(this.dataset.formId);
    (this.productForm || document).addEventListener('variant:change', this.onVariantChange);
    this.querySelector('[data-logeski-chat]')?.addEventListener('click', this.onChatClick);
    this.updateCheckoutLink();
  }

  disconnectedCallback() {
    (this.productForm || document).removeEventListener('variant:change', this.onVariantChange);
    this.querySelector('[data-logeski-chat]')?.removeEventListener('click', this.onChatClick);
  }

  onVariantChange(event) {
    if (!event.detail?.variant) return;
    this.variantId = event.detail.variant.id;
    this.dataset.variantId = this.variantId;
    this.updateCheckoutLink();

    const price = this.querySelector('[data-logeski-price]');
    if (price && event.detail.variant.price != null) {
      price.textContent = this.formatMoney(event.detail.variant.price);
    }
  }

  updateCheckoutLink() {
    const buyNow = this.querySelector('[data-logeski-buy-now]');
    if (!buyNow || !this.variantId) return;
    buyNow.href = `${window.Shopify?.routes?.root || '/'}cart/${this.variantId}:1?checkout`;
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'IDR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'id-ID', {
        style: 'currency',
        currency,
        maximumFractionDigits: currency === 'IDR' ? 0 : 2
      }).format(cents / 100);
    } catch (error) {
      return `${currency} ${(cents / 100).toFixed(2)}`;
    }
  }

  onChatClick() {
    const directUrl = this.dataset.chatUrl;
    if (directUrl) {
      window.location.href = directUrl;
      return;
    }

    const chatButton = this.findChatButton(document);
    if (chatButton) chatButton.click();
  }

  findChatButton(root) {
    const selectors = [
      '#ShopifyChat button',
      '[data-testid="chat-toggle"]',
      '[aria-label*="chat" i]',
      '[title*="chat" i]'
    ];

    for (const selector of selectors) {
      const button = root.querySelector?.(selector);
      if (button && !this.contains(button)) return button;
    }

    for (const element of root.querySelectorAll?.('*') || []) {
      if (element.shadowRoot) {
        const button = this.findChatButton(element.shadowRoot);
        if (button) return button;
      }
    }

    return null;
  }
}

if (!customElements.get('logeski-mobile-actions')) {
  customElements.define('logeski-mobile-actions', LogeskiMobileActions);
}

const logeskiReviewExpandLabel = () => {
  const language = document.documentElement.lang.toLowerCase();
  if (language.startsWith('th')) return 'ดูรีวิวทั้งหมด';
  if (language.startsWith('id')) return 'Lihat semua ulasan';
  return 'View all reviews';
};

const setupLogeskiReviewCollapse = (root) => {
  const list = root.querySelector('.jdgm-review-list');
  if (!list) return;

  const items = [...list.querySelectorAll(':scope > .jm-review-item')];
  const signature = items.map((item) => item.getAttribute('review-uuid') || '').join('|');
  const changedPage = root.dataset.logeskiReviewSignature !== signature;

  if (changedPage) {
    root.dataset.logeskiReviewSignature = signature;
    if (!root.hasAttribute('data-logeski-reviews-expanded')) {
      root.dataset.logeskiReviewsExpanded = 'false';
    }
  }

  let button = root.querySelector('.logeski-review-expand');

  if (items.length <= 3) {
    root.classList.remove('logeski-reviews-collapsed');
    button?.remove();
    return;
  }

  if (!button) {
    button = document.createElement('button');
    button.type = 'button';
    button.className = 'logeski-review-expand';
    button.textContent = logeskiReviewExpandLabel();
    button.setAttribute('aria-expanded', 'false');
    list.insertAdjacentElement('afterend', button);
    button.addEventListener('click', () => {
      root.dataset.logeskiReviewsExpanded = 'true';
      root.classList.remove('logeski-reviews-collapsed');
      button.setAttribute('aria-expanded', 'true');
      button.hidden = true;
    });
  }

  const expanded = root.dataset.logeskiReviewsExpanded === 'true';
  root.classList.toggle('logeski-reviews-collapsed', !expanded);
  button.hidden = expanded;
  button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
};

const initializeLogeskiReviewWidgets = () => {
  document.querySelectorAll('#judgeme_product_reviews').forEach((root) => {
    setupLogeskiReviewCollapse(root);
    if (root.dataset.logeskiReviewObserver === 'true') return;
    root.dataset.logeskiReviewObserver = 'true';
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        setupLogeskiReviewCollapse(root);
      });
    });
    observer.observe(root, { childList: true, subtree: true });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeLogeskiReviewWidgets, { once: true });
} else {
  initializeLogeskiReviewWidgets();
}

const logeskiReviewBootObserver = new MutationObserver(initializeLogeskiReviewWidgets);
logeskiReviewBootObserver.observe(document.documentElement, { childList: true, subtree: true });
